import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Logo } from '../ui'
import {
  REQUEST_PASSWORD_RESET_MUTATION,
  type UserPasswordResetRequestResponse,
  type UserPasswordResetRequestInput,
} from '../../graphql/mutations/passwordReset'
import { useAuth } from '../../contexts/AuthContext/AuthContext'
import { ROOT_PATH } from '../../config/rootPath'

export function PasswordResetRequestPage() {
  const [email, setEmail] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const [requestPasswordReset, { loading: _mutationLoading }] = useMutation<UserPasswordResetRequestResponse, { input: UserPasswordResetRequestInput }>(
    REQUEST_PASSWORD_RESET_MUTATION,
    {
      onCompleted: (data) => {
        if (data.userPasswordResetRequest.success) {
          setSuccessMessage('Check your email for a link to reset your password.')
        } else if (data.userPasswordResetRequest.errors && data.userPasswordResetRequest.errors.length > 0) {
          setFieldErrors({ email: data.userPasswordResetRequest.errors.join(', ') })
        }
        setIsSubmitting(false)
      },
      onError: (_error) => {
        setFieldErrors({ email: 'Unable to send reset link. Please try again later.' })
        setIsSubmitting(false)
      },
    }
  )

  const { requestPasswordReset: _authRequestPasswordReset } = useAuth()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const errors: Record<string, string> = {}

    if (!email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Invalid email format'
    }

    setFieldErrors(errors)
    
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true)
      setSuccessMessage('')
      
      try {
        await requestPasswordReset({variables: {input: {email: email.trim()}}})
      } catch (error) {
        setFieldErrors({ email: 'An unexpected error occurred' })
        setIsSubmitting(false)
      }
    }
  }

  return (
    <>
      <div className="relative flex min-h-screen shrink-0 justify-center md:px-12 lg:px-0">
        <div className="relative z-10 flex flex-1 flex-col bg-white px-4 py-10 shadow-2xl sm:justify-center md:flex-none md:px-28">
          <main className="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0">
            <div className="flex">
              <a aria-label="Home" href={ROOT_PATH}>
                <Logo variant="image" showText={true} size="md" />
              </a>
            </div>
            <h2 className="mt-20 text-lg font-semibold text-gray-900">Forgot your password?</h2>
            <p className="mt-2 text-sm text-gray-700">
              Enter your email address below and we'll send you a link to reset your password.
            </p>

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-md mt-4">
                <p className="text-sm font-medium">{successMessage}</p>
              </div>
            )}
            
            <form className="mt-10 grid grid-cols-1 gap-y-8" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="mb-3 block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                  name="email"
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                )}
              </div>
              <div>
                <button
                  className="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600 w-full"
                  type="submit"
                  disabled={isSubmitting}
                >
                  <span>
                    {isSubmitting ? 'Sending...' : 'Send reset link'} <span aria-hidden="true">→</span>
                  </span>
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <a href="/login" className="text-sm text-blue-600 hover:underline">
                Back to login
              </a>
            </div>
          </main>
        </div>
        <div className="hidden sm:contents lg:relative lg:block lg:flex-1">
          <img
            alt=""
            loading="lazy"
            width="1664"
            height="1866"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ color: 'transparent' }}
            src="/background-auth.jpg"
          />
        </div>
      </div>
    </>
  )
} 